import { Button, Modal } from 'antd';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotalSelectedStyle } from './styles';
import ProductTable from './ProductTable';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { useGetFilter } from 'hooks/useSearchParamsObj';

type SearchProductModalProps = {
  model?: string;
  initSelectedRows?: React.Key[];
  onAdd: (selectedRows: React.Key[]) => void;
  loading?: boolean;
  columns?: IRestColumnProps[];
  initialFilter?: FilterFromUrlResponseType;
  expandable?: DynamicObjectType;
  formatListData?: (data: DynamicObjectType[]) => DynamicObjectType[];
  searchKeys?: string[];
  customTitle?: string;
  placeholderInputSearch?: string;
};

const SearchProductModal = (
  {
    model,
    initSelectedRows,
    onAdd,
    loading,
    columns,
    initialFilter,
    formatListData,
    expandable,
    searchKeys,
    customTitle,
    placeholderInputSearch,
  }: SearchProductModalProps,
  ref,
) => {
  const { t } = useTranslation();
  const { filter, searchParams, setSearchParams } = useGetFilter();
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);
  const [conditionsBeforeOpenModal, setConditionsBeforeOpenModal] =
    useState<DynamicObjectType>();
  const [open, setOpen] = useState(false);

  const handleAdd = () => {
    onAdd?.(selectedRows);
    if (!loading) {
      handleCancel();
    }
  };

  const handleCancel = () => {
    if (Object.keys(filter)?.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [key, value] of Object.entries(filter)) {
        searchParams.delete(key);        
        setSearchParams(searchParams);
      }
    }
    if (conditionsBeforeOpenModal) {
      for (const [key, value] of Object.entries(conditionsBeforeOpenModal)) {
        searchParams.set(key, value);
        setSearchParams(searchParams);
      }
    }
    setSelectedRows([]);
    setOpen(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (value) => {
        if (Object.keys(filter)?.length > 0) {
          const result = {};
          for (const [key, value] of Object.entries(filter)) {
            if (value) {
              result[key] = value;
            }
            searchParams.delete(key);
          }
          setConditionsBeforeOpenModal(result);
          setSearchParams(searchParams);
        }
        setOpen(value);
      },
    }),
    [filter], // eslint-disable-line
  );

  useEffect(() => {
    if (open && !!initSelectedRows?.length) {
      setSelectedRows(initSelectedRows);
    }
  }, [open, initSelectedRows]);

  return (
    <Modal
      title={customTitle || t('products.addProduct')}
      footer={null}
      open={open}
      width="900px"
      onCancel={handleCancel}
      destroyOnClose={true}
    >
      {!!selectedRows.length && (
        <TotalSelectedStyle>
          <div className="">
            {t('products.selected', { count: selectedRows?.length || 0 })}
          </div>
          <Button
            loading={loading}
            onClick={handleAdd}
            className="btn-primary-light btn-h-32"
          >
            {t('button.add')}
          </Button>
        </TotalSelectedStyle>
      )}
      <ProductTable
        model={model}
        columns={columns}
        initSelectedRowKeys={selectedRows}
        handleSelectedRowKeys={setSelectedRows}
        initialFilter={initialFilter}
        formatListData={formatListData}
        expandable={expandable}
        searchKeys={searchKeys}
        placeholderInputSearch={placeholderInputSearch}
      />
    </Modal>
  );
};

export default React.forwardRef(SearchProductModal);
